import {
	BUTTON_STYLES,
	type ButtonKind,
	type ButtonStyle,
	INLINE_BUTTON_KINDS,
	type KeyboardType,
	MAX_BUTTON_LABEL_LENGTH,
	MAX_BUTTONS,
	MAX_BUTTONS_PER_ROW,
	MAX_COPY_TEXT_LENGTH,
	MAX_KEYBOARD_PLACEHOLDER_LENGTH,
	REPLY_BUTTON_KINDS,
} from "@velizhanin-dev/bot-scenario"
import { ArrowDown, ArrowUp, CornerDownLeft, Plus, Trash2 } from "lucide-react"
import { makeButtonId } from "@/entities/bot-builder"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Checkbox } from "@/shared/ui/checkbox"
import { Field, IssueText } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { Label } from "@/shared/ui/label"
import { NativeSelect } from "@/shared/ui/native-select"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { TextWithVariables } from "@/shared/ui/text-with-variables"
import { ToolButton } from "@/shared/ui/tool-button"
import { VariableSelect } from "@/shared/ui/variable-select"
import { type Config, type EditorProps, readString, withField } from "./editor-props"
import { issuesAt } from "./issues-text"
import { layoutRows } from "./lib/layout-rows"
import { MessageBody } from "./message-body"
import { SendOptionsFields } from "./send-options-fields"
import { TimeoutField } from "./timeout-field"
import { AnimatePresence, motion } from "framer-motion"
import { listItem, TRANSITION } from "@/shared/lib/motion"
import { AutoHeight } from "@/shared/ui/auto-height"
import { Reveal } from "@/shared/ui/reveal"

interface ButtonDraft {
	id: string
	label: string
	kind: ButtonKind
	url?: string
	copyText?: string
	variable?: string
	style?: ButtonStyle
	newRow?: boolean
	continueOnOpen?: boolean
}

const STYLE_LABELS: Record<ButtonStyle, string> = {
	primary: "Синяя",
	success: "Зелёная",
	danger: "Красная",
}

const STYLE_CLASSES: Record<ButtonStyle, string> = {
	primary: "border-l-4 border-l-blue-500",
	success: "border-l-4 border-l-emerald-500",
	danger: "border-l-4 border-l-red-500",
}

const BUTTON_KIND_LABELS: Record<ButtonKind, string> = {
	branch: "Ведёт к блоку",
	url: "Ссылка",
	web_app: "Мини-приложение",
	copy: "Скопировать текст",
	contact: "Поделиться контактом",
	location: "Отправить геолокацию",
}

const KEYBOARD_LABELS: Record<KeyboardType, string> = {
	inline: "Под сообщением",
	reply: "Вместо клавиатуры",
}

function readButtons(config: EditorProps["config"]): ButtonDraft[] {
	const buttons = config.buttons
	return Array.isArray(buttons) ? (buttons as ButtonDraft[]) : []
}

/** Оставляет у кнопки только поля её типа. */
function normalizeButton(button: ButtonDraft): ButtonDraft {
	const { url, copyText, variable, style, newRow, continueOnOpen, ...kindless } = button
	const base = { ...kindless, ...(style ? { style } : {}), ...(newRow ? { newRow } : {}) }
	if (button.kind === "url")
		return { ...base, url: url ?? "", ...(continueOnOpen ? { continueOnOpen } : {}) }
	if (button.kind === "web_app") return { ...base, url: url ?? "" }
	if (button.kind === "copy") return { ...base, copyText: copyText ?? "" }
	if (button.kind === "contact" || button.kind === "location")
		return variable ? { ...base, variable } : base
	return base
}

/** Текст вместо нажатия: свой выход и сохранение ответа, как «свободный ответ» в воронках Botmaker. */
function TextReplyField({ config, onChange, issues, draft }: EditorProps) {
	const textReply = config.textReply as { saveTo?: string } | undefined
	return (
		<Field
			label="Если написал текстом"
			issues={issuesAt(issues, "textReply", true)}
			hint={
				textReply
					? "Бот пойдёт по выходу «Ответил текстом». Подпись кнопки текстом по-прежнему нажимает кнопку."
					: "Без этого текст вместо нажатия уходит в воронку «Не понял», а бот продолжает ждать кнопку."
			}
		>
			<Label className="flex items-center gap-2 text-sm">
				<Checkbox
					checked={!!textReply}
					onCheckedChange={(checked) =>
						onChange(withField(config, "textReply", checked === true ? {} : undefined))
					}
				/>
				Принимать ответ текстом
			</Label>
			{textReply ? (
				<VariableSelect
					aria-label="Куда сохранить текст"
					value={textReply.saveTo ?? ""}
					onChange={(name) => onChange({ ...config, textReply: name ? { saveTo: name } : {} })}
					variables={draft.variables}
					emptyLabel="Не сохранять текст"
				/>
			) : null}
		</Field>
	)
}

export function ButtonsEditor(props: EditorProps) {
	const { config, onChange, issues, draft } = props
	const buttons = readButtons(config)
	const keyboard: KeyboardType = config.keyboard === "reply" ? "reply" : "inline"
	const allowedKinds = keyboard === "reply" ? REPLY_BUTTON_KINDS : INLINE_BUTTON_KINDS
	const columns =
		typeof config.columns === "number" && config.columns >= 1
			? Math.min(Math.floor(config.columns), MAX_BUTTONS_PER_ROW)
			: 1
	const rows = layoutRows(buttons, columns)

	const setButtons = (next: ButtonDraft[]) => onChange({ ...config, buttons: next })
	const update = (index: number, patch: Partial<ButtonDraft>) =>
		setButtons(
			buttons.map((button, i) => (i === index ? normalizeButton({ ...button, ...patch }) : button)),
		)
	const move = (index: number, delta: number) => {
		const target = index + delta
		if (target < 0 || target >= buttons.length) return
		const next = [...buttons]
		;[next[index], next[target]] = [next[target], next[index]]
		setButtons(next)
	}
	const setKeyboard = (next: KeyboardType) => {
		const kinds = next === "reply" ? REPLY_BUTTON_KINDS : INLINE_BUTTON_KINDS
		// Кнопки, которых нет в новом виде клавиатуры, становятся обычными ветками.
		const converted = buttons.map((button) =>
			kinds.has(button.kind) ? button : normalizeButton({ ...button, kind: "branch" }),
		)
		const base = { ...config, keyboard: next === "inline" ? undefined : next, buttons: converted }
		const inline = ["keyboard", "oneTime", "persistent", "placeholder"].reduce<Config>(
			(acc, key) => withField(acc, key, undefined),
			base,
		)
		onChange(next === "inline" ? inline : base)
	}

	return (
		<div className="space-y-4">
			<MessageBody {...props} allowGallery />

			<Field
				label="Где показать кнопки"
				hint={
					keyboard === "reply"
						? "Кнопки заменят клавиатуру телефона. Так можно попросить контакт или геолокацию."
						: "Кнопки под сообщением: ветки, ссылки, мини-приложение, копирование."
				}
			>
				<SegmentedControl
					aria-label="Где показать кнопки"
					options={(["inline", "reply"] as const).map((value) => ({
						value,
						label: KEYBOARD_LABELS[value],
					}))}
					value={keyboard}
					onChange={(value) => value && setKeyboard(value)}
				/>
			</Field>

			<Field label="Кнопок в ряд" hint="Отдельную кнопку можно перенести на новый ряд значком ↵">
				<SegmentedControl
					aria-label="Кнопок в ряд"
					options={Array.from({ length: MAX_BUTTONS_PER_ROW }, (_, i) => ({
						value: i + 1,
						label: i + 1,
					}))}
					value={columns}
					onChange={(value) => value && onChange({ ...config, columns: value })}
				/>
			</Field>

			{buttons.length > 0 ? (
				<AutoHeight>
					<div
						className="space-y-1 rounded-lg bg-muted/40 p-2"
						role="group"
						aria-label="Как увидит пользователь"
					>
						{rows.map((row, rowIndex) => (
							<div key={rowIndex} className="flex gap-1">
								{row.map((button) => (
									// Смена числа кнопок в ряд перекладывает кнопки, а не перескакивает.
									<motion.span
										layout
										transition={{ layout: TRANSITION.slow }}
										key={button.id}
										className={cn(
											"min-w-0 flex-1 truncate rounded-md bg-background px-2 py-1 text-center text-xs",
											button.style && STYLE_CLASSES[button.style],
										)}
									>
										{button.label || "…"}
									</motion.span>
								))}
							</div>
						))}
					</div>
				</AutoHeight>
			) : null}

			{/* Настройки клавиатуры раскрываются по высоте. Обёртка общая с кнопками ниже,
			    чтобы отступ списка не появлялся рывком. */}
			<div>
				<Reveal show={keyboard === "reply"} className="pb-4">
					<div className="space-y-2">
						<Label className="flex items-center gap-2 text-sm">
							<Checkbox
								checked={config.oneTime === true}
								onCheckedChange={(checked) =>
									onChange(withField(config, "oneTime", checked === true || undefined))
								}
							/>
							Спрятать клавиатуру после нажатия
						</Label>
						<Label className="flex items-center gap-2 text-sm">
							<Checkbox
								checked={config.persistent === true}
								onCheckedChange={(checked) =>
									onChange(withField(config, "persistent", checked === true || undefined))
								}
							/>
							Не давать свернуть клавиатуру
						</Label>
						<Input
							aria-label="Подсказка в поле ввода"
							placeholder="Подсказка в поле ввода, например «Выберите раздел»"
							maxLength={MAX_KEYBOARD_PLACEHOLDER_LENGTH}
							value={readString(config, "placeholder")}
							onChange={(event) =>
								onChange(withField(config, "placeholder", event.target.value || undefined))
							}
						/>
					</div>
				</Reveal>

				<div>
					<div className="mb-2 flex items-center justify-between gap-3">
						<h3 className="text-sm font-medium text-foreground/90">Кнопки</h3>
						<span className="text-xs tabular-nums text-muted-foreground">
							{buttons.length} из {MAX_BUTTONS}
						</span>
					</div>
					<ul>
						<AnimatePresence initial={false}>
							{buttons.map((button, index) => (
								<motion.li key={button.id} {...listItem}>
									<div className="pb-2">
										<div className="space-y-2 rounded-xl border border-border/60 p-3">
											<div className="flex items-center gap-1">
												<Input
													aria-label={`Подпись кнопки ${index + 1}`}
													placeholder="Например, Записаться"
													value={button.label}
													maxLength={MAX_BUTTON_LABEL_LENGTH}
													onChange={(event) => update(index, { label: event.target.value })}
												/>
												<ToolButton
													label="Выше"
													className="size-9"
													disabled={index === 0}
													onClick={() => move(index, -1)}
												>
													<ArrowUp />
												</ToolButton>
												<ToolButton
													label="Ниже"
													className="size-9"
													disabled={index === buttons.length - 1}
													onClick={() => move(index, 1)}
												>
													<ArrowDown />
												</ToolButton>
												<ToolButton
													label={`Удалить кнопку ${index + 1}`}
													className="size-9 hover:bg-red-500/15 hover:text-red-300"
													onClick={() => setButtons(buttons.filter((_, i) => i !== index))}
												>
													<Trash2 />
												</ToolButton>
											</div>
											<NativeSelect
												aria-label={`Тип кнопки ${index + 1}`}
												value={button.kind}
												onChange={(event) =>
													update(index, { kind: event.target.value as ButtonKind })
												}
											>
												{(Object.keys(BUTTON_KIND_LABELS) as ButtonKind[])
													.filter((kind) => allowedKinds.has(kind) || kind === button.kind)
													.map((kind) => (
														<option key={kind} value={kind}>
															{BUTTON_KIND_LABELS[kind]}
														</option>
													))}
											</NativeSelect>
											<div className="flex items-center gap-1">
												<NativeSelect
													aria-label={`Цвет кнопки ${index + 1}`}
													className="h-8 flex-1 text-xs"
													value={button.style ?? ""}
													onChange={(event) =>
														update(index, {
															style: (event.target.value || undefined) as ButtonStyle | undefined,
														})
													}
												>
													<option value="">Цвет как в Telegram</option>
													{BUTTON_STYLES.map((style) => (
														<option key={style} value={style}>
															{STYLE_LABELS[style]}
														</option>
													))}
												</NativeSelect>
												<Button
													type="button"
													size="sm"
													variant={button.newRow ? "secondary" : "ghost"}
													className="h-8"
													aria-pressed={button.newRow === true}
													title="Начать с этой кнопки новый ряд"
													disabled={index === 0}
													onClick={() =>
														update(index, { newRow: button.newRow ? undefined : true })
													}
												>
													<CornerDownLeft />
													Новый ряд
												</Button>
											</div>
											{button.kind === "url" || button.kind === "web_app" ? (
												<Input
													aria-label={`Ссылка кнопки ${index + 1}`}
													placeholder="https://…"
													inputMode="url"
													value={button.url ?? ""}
													onChange={(event) => update(index, { url: event.target.value })}
												/>
											) : null}
											{button.kind === "url" && keyboard === "inline" ? (
												<Label className="flex items-start gap-2 text-xs text-muted-foreground">
													<Checkbox
														className="mt-0.5"
														checked={button.continueOnOpen === true}
														onCheckedChange={(checked) =>
															update(index, { continueOnOpen: checked === true || undefined })
														}
													/>
													<span>
														Продолжать сценарий, когда откроют ссылку. У кнопки появится свой выход.
														Ссылка пойдёт через наш сервер, он засчитает переход.
													</span>
												</Label>
											) : null}
											{button.kind === "copy" ? (
												<TextWithVariables
													multiline={false}
													placeholder="Промокод, реквизиты, ссылка…"
													maxLength={MAX_COPY_TEXT_LENGTH}
													value={button.copyText ?? ""}
													onChange={(copyText) => update(index, { copyText })}
													variables={draft.variables}
												/>
											) : null}
											{button.kind === "contact" || button.kind === "location" ? (
												<VariableSelect
													aria-label={`Куда сохранить ${button.kind === "contact" ? "телефон" : "геолокацию"}`}
													value={button.variable ?? ""}
													onChange={(variable) =>
														update(index, { variable: variable || undefined })
													}
													variables={draft.variables}
													emptyLabel={
														button.kind === "contact"
															? "Не сохранять телефон"
															: "Не сохранять геолокацию"
													}
												/>
											) : null}
											<IssueText issues={issuesAt(issues, `buttons.${index}`, true)} />
										</div>
									</div>
								</motion.li>
							))}
						</AnimatePresence>
					</ul>
					<IssueText issues={issuesAt(issues, "buttons")} />
					<Button
						type="button"
						variant="outline"
						size="sm"
						className={cn("rounded-full", buttons.length >= MAX_BUTTONS && "hidden")}
						onClick={() =>
							setButtons([
								...buttons,
								{ id: makeButtonId(), label: `Кнопка ${buttons.length + 1}`, kind: "branch" },
							])
						}
					>
						<Plus />
						Добавить кнопку
					</Button>
				</div>
			</div>

			<TextReplyField {...props} />
			<TimeoutField config={config} onChange={onChange} what="нажатия" />
			<SendOptionsFields config={config} onChange={onChange} />
		</div>
	)
}
